import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { apiFetch } from '@/hooks/useFetch';

const LAST_POTD_KEY = 'clearcomm-last-potd-notif';
const LAST_TODO_KEY = 'clearcomm-last-todo-notif';
const LAST_STREAK_KEY = 'clearcomm-last-streak-notif';
const CHECK_INTERVAL = 60 * 1000; // Check every minute

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
};

const currentHHMM = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const isTimeReached = (targetTime) => {
  return currentHHMM() >= targetTime;
};

const sendToSW = (data) => {
  if (navigator.serviceWorker?.controller) {
    navigator.serviceWorker.controller.postMessage(data);
    return true;
  }
  return false;
};

const showLocalNotification = (title, body) => {
  if (Notification.permission === 'granted') {
    new Notification(title, {
      body,
      icon: '/icon-pixel-192.png',
      badge: '/icon-pixel-192.png'
    });
  }
};

export const useNotificationScheduler = () => {
  const { isAuthenticated } = useAuth();
  const intervalRef = useRef(null);

  const checkAndNotify = useCallback(async () => {
    if (!isAuthenticated) return;
    if (Notification.permission !== 'granted') return;

    const today = todayStr();

    // Fetch user preferences
    let prefs;
    try {
      const { data } = await apiFetch('/api/notifications/preferences');
      prefs = data;
    } catch {
      return;
    }
    if (!prefs) return;

    // 1. Phrase of the Day notification
    if (prefs.phrase_of_day) {
      const lastPotd = localStorage.getItem(LAST_POTD_KEY);
      const potdTime = prefs.potd_time || '09:00';

      if (lastPotd !== today && isTimeReached(potdTime)) {
        try {
          const { data: phrase } = await apiFetch('/api/phrases/phrase-of-the-day');
          if (phrase) {
            const title = 'Phrase of the Day!';
            const body = `"${phrase.phrase}" — Tap to see what it really means`;
            const sent = sendToSW({
              type: 'SHOW_NOTIFICATION',
              title,
              body,
              tag: 'potd',
              url: `/phrase/${phrase.id}`
            });
            if (!sent) showLocalNotification(title, body);
            localStorage.setItem(LAST_POTD_KEY, today);
          }
        } catch { /* silent */ }
      }
    }

    // 2. To-do list reminder
    if (prefs.todo_reminders) {
      const lastTodo = localStorage.getItem(LAST_TODO_KEY);

      if (lastTodo !== today && isTimeReached(prefs.potd_time || '09:00')) {
        try {
          const raw = localStorage.getItem('clearcomm-todos');
          const todos = raw ? JSON.parse(raw) : [];
          const pending = todos.filter(t => !t.completed);

          if (pending.length > 0) {
            const title = 'To-Do List Reminder';
            const body = `You have ${pending.length} pending task${pending.length > 1 ? 's' : ''} on your list`;
            const sent = sendToSW({
              type: 'SHOW_NOTIFICATION',
              title,
              body,
              tag: 'todo-reminder',
              url: '/todo'
            });
            if (!sent) showLocalNotification(title, body);
            localStorage.setItem(LAST_TODO_KEY, today);
          }
        } catch { /* silent */ }
      }
    }

    // 3. Streak reminder
    if (prefs.streak_reminders) {
      const lastStreak = localStorage.getItem(LAST_STREAK_KEY);
      const streakTime = prefs.reminder_time || '20:00';

      if (lastStreak !== today && isTimeReached(streakTime)) {
        try {
          const { data: status } = await apiFetch('/api/notifications/streak-status');
          if (status?.streak_at_risk) {
            const title = 'Streak at Risk!';
            const body = `Your ${status.current_streak}-day streak is at risk! Open the app to keep it going.`;
            const sent = sendToSW({
              type: 'SHOW_NOTIFICATION',
              title,
              body,
              tag: 'streak-reminder',
              url: '/library'
            });
            if (!sent) showLocalNotification(title, body);
            localStorage.setItem(LAST_STREAK_KEY, today);
          }
        } catch { /* silent */ }
      }
    }

    // 4. Friend activity (level-ups, badges, streak hits ≥3 days)
    if (prefs.friend_activity) {
      try {
        const { data: friendActivity } = await apiFetch('/api/notifications/friend-activity');
        const list = friendActivity?.notifications || [];
        for (const n of list.slice(0, 3)) {  // Cap at 3 to avoid spam
          const sent = sendToSW({
            type: 'SHOW_NOTIFICATION',
            title: n.title,
            body: n.body,
            tag: n.tag,
            url: n.data?.url || '/friends'
          });
          if (!sent) showLocalNotification(n.title, n.body);
        }
      } catch { /* silent */ }
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;

    // Initial check after short delay
    const timeout = setTimeout(checkAndNotify, 5000);

    // Periodic check
    intervalRef.current = setInterval(checkAndNotify, CHECK_INTERVAL);

    return () => {
      clearTimeout(timeout);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isAuthenticated, checkAndNotify]);
};
